/* @flow */

import * as React from 'react';
import copy from 'copy-to-clipboard';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/free-solid-svg-icons';
import { FacebookShareButton, TwitterShareButton } from 'react-share';

import t from 'app/utils/translate';
import EmailShareModal from 'app/components/commons/EmailShareModal';
import Icon from 'app/components/commons/Icon';
import { COPY_COLOR, FACEBOOK_COLOR, TWITTER_COLOR } from 'app/constants/socialColorTypes';
import withNotificationCallback from 'app/components/notifications/withNotificationCallback';
import SocialButton from './SocialButton';

type Props = {
  challengeUrl?: string,
  className?: string,
  clipboard?: boolean,
  email?: boolean,
  emailTitle?: string,
  emailErrors: Array<*>,
  facebook?: boolean,
  facebookTitle?: string,
  facebookUrl?: string,
  isSharing: boolean,
  onEmailSubmit: Object => void,
  transmitNotification: Object => void,
  twitter?: boolean,
  twitterHashtags?: Array<string>,
  twitterTitle?: string,
  twitterUrl?: string,
  twitterVia?: string,
  vibrant?: boolean
};

type State = {
  emailModalIsOpen: boolean
};

const FacebookButton = SocialButton.withComponent(FacebookShareButton);
const TwitterButton = SocialButton.withComponent(TwitterShareButton);

const Container = styled.div({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center'
});

const Label = styled.span(({ theme }) => ({
  fontSize: 'inherit'
}));

class SocialShareButtons extends React.Component<Props, State> {
  props: Props;

  state: State = {
    emailModalIsOpen: false
  };

  static defaultProps = {
    challengeUrl: undefined,
    className: undefined,
    clipboard: false,
    email: false,
    emailTitle: '',
    facebook: false,
    facebookTitle: '',
    facebookUrl: window.location.href,
    twitter: false,
    twitterHashtags: [],
    twitterTitle: '',
    twitterUrl: window.location.href,
    twitterVia: '',
    vibrant: false
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { isSharing, transmitNotification } = this.props;

    if (isSharing && !nextProps.isSharing && nextProps.emailErrors.length === 0) {
      this.closeEmailModal();
      transmitNotification({
        message: t('Share successful'),
        type: 'success'
      });
    }
  }

  handleCopy = () => {
    const { challengeUrl, transmitNotification } = this.props;
    copy(challengeUrl || window.location.href);
    transmitNotification({
      message: t('Url copied to clipboard'),
      type: 'success'
    });
  };

  openEmailModal = () => {
    this.setState({
      emailModalIsOpen: true
    });
  };

  closeEmailModal = () => {
    this.setState({
      emailModalIsOpen: false
    });
  };

  render() {
    const {
      className,
      clipboard,
      email,
      emailTitle = '',
      emailErrors,
      facebook,
      facebookTitle,
      facebookUrl,
      onEmailSubmit,
      twitter,
      twitterHashtags,
      twitterTitle,
      twitterUrl,
      twitterVia,
      vibrant
    } = this.props;
    const { emailModalIsOpen } = this.state;

    return (
      <Container className={className}>
        {facebook && (
          <FacebookButton
            backgroundColor={FACEBOOK_COLOR}
            quote={facebookTitle}
            url={facebookUrl}
            vibrant={vibrant}
          >
            <Icon type="icon-social-facebook" />
          </FacebookButton>
        )}
        {twitter && (
          <TwitterButton
            backgroundColor={TWITTER_COLOR}
            hashtags={twitterHashtags}
            title={twitterTitle}
            url={twitterUrl}
            via={twitterVia}
            vibrant={vibrant}
          >
            <Icon type="icon-social-twitter" />
          </TwitterButton>
        )}
        {email && (
          <SocialButton onClick={this.openEmailModal} vibrant={vibrant}>
            <Icon type="icon-email" />
          </SocialButton>
        )}
        {clipboard && (
          <SocialButton
            backgroundColor={COPY_COLOR}
            onClick={this.handleCopy}
            vibrant={vibrant}
            pad
          >
            <Label>COPY &amp; SHARE&nbsp;&nbsp;</Label>
            <FontAwesomeIcon icon={faLink} size="sm" />
          </SocialButton>
        )}
        {emailModalIsOpen && (
          <EmailShareModal
            title={emailTitle}
            errors={emailErrors}
            onCancel={this.closeEmailModal}
            onSubmit={onEmailSubmit}
          />
        )}
      </Container>
    );
  }
}

export default withNotificationCallback(SocialShareButtons);
